import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Trash2, Star, Loader2, Upload, ClipboardPaste, Camera, Users, Download, FileText, FileSpreadsheet, GitMerge } from 'lucide-react';
import { compressImageForUpload, uploadImageWithRetry } from '@/lib/imageUtils';
import { extractErrorMessage } from '@/lib/errorUtils';
import { MergePlayersDialog } from './MergePlayersDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY_ROW = { name: '', jersey_number: '', headshot_path: '', is_captain: false };

export function EditRosterDialog({ open, onOpenChange, eventId, team, onSaved }) {
  const [rows, setRows] = useState([]);
  const [teamPhoto, setTeamPhoto] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [activeUploadIdx, setActiveUploadIdx] = useState(-1);
  const [uploadingTeamPhoto, setUploadingTeamPhoto] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const fileInputRef = useRef(null);
  const teamPhotoInputRef = useRef(null);

  useEffect(() => {
    if (!open || !eventId || !team || !team.id) return;
    setLoading(true);
    const token = localStorage.getItem('session_token');
    fetch(`${API_URL}/api/events/${eventId}/teams/${team.id}/roster-detail`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) {
          setRows([{ ...EMPTY_ROW }]);
          setTeamPhoto('');
          return;
        }
        const data = await res.json();
        const players = data.players || [];
        setRows(players.length > 0
          ? players.map(p => ({
              name: p.name || '',
              jersey_number: p.jersey_number || '',
              headshot_path: p.headshot_path || '',
              is_captain: !!p.is_captain,
            }))
          : [{ ...EMPTY_ROW }]);
        setTeamPhoto(data.team_photo || '');
      })
      .catch(() => { setRows([{ ...EMPTY_ROW }]); setTeamPhoto(''); })
      .finally(() => setLoading(false));
  }, [open, eventId, team]);

  const update = (i, field, val) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const addRow = () => setRows(rs => [...rs, { ...EMPTY_ROW }]);
  const removeRow = (i) => setRows(rs => rs.length === 1 ? [{ ...EMPTY_ROW }] : rs.filter((_, idx) => idx !== i));

  const triggerUpload = (i) => {
    setActiveUploadIdx(i);
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const original = event.target.files && event.target.files[0];
    const idx = activeUploadIdx;
    if (event.target) event.target.value = '';
    if (!original || idx < 0) return;
    const token = localStorage.getItem('session_token');
    try {
      const data = await uploadImageWithRetry(
        API_URL, '/api/upload/headshot', original, token
      );
      update(idx, 'headshot_path', data.storage_path || '');
      toast.success('Headshot uploaded');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Upload failed'));
    } finally {
      setActiveUploadIdx(-1);
    }
  };

  const handleTeamPhotoChange = async (event) => {
    const original = event.target.files && event.target.files[0];
    if (event.target) event.target.value = '';
    if (!original) return;
    const token = localStorage.getItem('session_token');
    setUploadingTeamPhoto(true);
    try {
      const data = await uploadImageWithRetry(
        API_URL, '/api/upload/team-photo', original, token
      );
      setTeamPhoto(data.storage_path || '');
      toast.success('Team photo uploaded');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Upload failed'));
    } finally {
      setUploadingTeamPhoto(false);
    }
  };

  const handlePasteCSV = async () => {
    const text = pasteText.trim();
    if (!text) { toast.error('Paste a roster CSV first'); return; }
    // Warn if existing manual rows would be overwritten
    const meaningfulRows = rows.filter(r => (r.name || '').trim()).length;
    if (meaningfulRows > 0) {
      const ok = window.confirm(`You have ${meaningfulRows} player${meaningfulRows === 1 ? '' : 's'} already entered. Importing the CSV will REPLACE all rows. Continue?`);
      if (!ok) return;
    }
    const token = localStorage.getItem('session_token');
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/teams/${team.id}/roster/parse-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ csv: text }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Parse failed');
      const parsed = data.players || [];
      if (parsed.length === 0) { toast.error('No valid rows found'); return; }
      setRows(parsed.map(p => ({
        name: p.name, jersey_number: p.jersey_number || '',
        headshot_path: '', is_captain: !!p.is_captain,
      })));
      toast.success(`Imported ${parsed.length} player${parsed.length === 1 ? '' : 's'}`);
      setPasteMode(false);
      setPasteText('');
    } catch (e) {
      toast.error(e.message || 'Parse failed');
    }
  };

  const save = async () => {
    const cleaned = rows
      .map(r => ({ ...r, name: r.name.trim(), jersey_number: (r.jersey_number || '').trim() }))
      .filter(r => r.name);
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/events/${eventId}/teams/${team.id}/roster`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ players: cleaned, team_photo: teamPhoto }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      toast.success(`Roster saved (${data.player_count} players)`);
      // Auto-merge nudge — if the backend detected a name edit on a same-jersey
      // player, it already rewrote historical stats_games to the new spelling.
      // Surface that to the user so they know past games followed the rename.
      const auto = data.auto_merge;
      if (auto && Array.isArray(auto.renames) && auto.renames.length > 0) {
        const r0 = auto.renames[0];
        const extra = auto.renames.length > 1 ? ` (+${auto.renames.length - 1} more)` : '';
        toast.success(
          `Auto-merged "${r0.old}" → "${r0.new}"${extra} across ${auto.games_updated} game${auto.games_updated === 1 ? '' : 's'}.`,
          { duration: 6000 },
        );
      }
      onOpenChange && onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Direct-download the current team's roster. Hits the auth-required
  // organizer export endpoint so it works with unsaved rosters too — we
  // trigger a save inline only if the caller hasn't committed changes.
  // For simplicity, we always fetch the SERVER's copy (what a coach would
  // see if they downloaded from a fresh session), matching the paste-CSV
  // pattern which also round-trips through the server.
  const exportRoster = async (fmt) => {
    if (!eventId || !team || !team.id) return;
    const token = localStorage.getItem('session_token');
    try {
      const res = await fetch(
        `${API_URL}/api/events/${eventId}/teams/${team.id}/roster/export?format=${fmt}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || `Export failed (HTTP ${res.status})`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(team.name || 'team').replace(/[^A-Za-z0-9._-]+/g, '_')}-Roster.${fmt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Roster downloaded as ${fmt.toUpperCase()}`);
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-roster-dialog">
        <DialogHeader>
          <DialogTitle>Edit Roster — {team && team.name}</DialogTitle>
          <DialogDescription>
            Add, edit, or remove players. Changes here override whatever the team contact submitted. Saving syncs to the team's roster page and to your "Saved Teams" library for future stats games.
          </DialogDescription>
        </DialogHeader>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
        <input ref={teamPhotoInputRef} type="file" accept="image/*" className="hidden" onChange={handleTeamPhotoChange} data-testid="team-photo-input" />
        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#00247D]" />
          </div>
        ) : (
          <div className="space-y-3">
            {/* Team Photo */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 flex items-center gap-4" data-testid="team-photo-section">
              <div className="shrink-0">
                {teamPhoto ? (
                  <img
                    src={`${API_URL}/api/files/${teamPhoto}`}
                    alt="Team"
                    className="w-24 h-16 object-cover rounded-md border border-slate-200 bg-white"
                    data-testid="team-photo-preview"
                  />
                ) : (
                  <div className="w-24 h-16 rounded-md border-2 border-dashed border-slate-300 bg-white flex items-center justify-center text-slate-400">
                    <Users className="w-6 h-6" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold uppercase tracking-wide text-[#00247D] flex items-center gap-1.5" style={{ fontFamily: 'Barlow Condensed' }}>
                  <Camera className="w-3.5 h-3.5" /> Team Photo
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5">Shown on the public roster page. Under 2MB, JPG or PNG.</p>
              </div>
              <div className="flex items-center gap-2">
                {teamPhoto && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setTeamPhoto('')}
                    disabled={uploadingTeamPhoto}
                    className="text-slate-500 hover:text-red-600"
                    data-testid="team-photo-remove-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => teamPhotoInputRef.current?.click()}
                  disabled={uploadingTeamPhoto}
                  data-testid="team-photo-upload-btn"
                >
                  {uploadingTeamPhoto ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Uploading…</>
                  ) : (
                    <><Upload className="w-3.5 h-3.5 mr-1" /> {teamPhoto ? 'Replace' : 'Upload'}</>
                  )}
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-1">
              <Button type="button" size="sm" variant="outline" onClick={() => setPasteMode(m => !m)} data-testid="paste-csv-toggle">
                <ClipboardPaste className="w-4 h-4 mr-1" /> {pasteMode ? 'Cancel paste' : 'Paste Roster CSV'}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" size="sm" variant="outline" data-testid="export-roster-trigger">
                    <Download className="w-4 h-4 mr-1" /> Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => exportRoster('csv')} data-testid="export-roster-csv">
                    <FileSpreadsheet className="w-4 h-4 mr-2" /> CSV (Excel / Sheets)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportRoster('pdf')} data-testid="export-roster-pdf">
                    <FileText className="w-4 h-4 mr-2" /> PDF (Printable)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setMergeOpen(true)}
                data-testid="merge-players-open-btn"
              >
                <GitMerge className="w-4 h-4 mr-1" /> Merge duplicates
              </Button>
              <span className="text-xs text-slate-500">Headers auto-mapped (name, jersey, captain). Replaces all current rows.</span>
            </div>
            {pasteMode && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2">
                <Textarea rows={6} value={pasteText} onChange={e => setPasteText(e.target.value)}
                  placeholder={"Paste from Excel/Google Sheets — example:\nName,Jersey,Captain\nJohn Doe,12,Yes\nJane Smith,7,\nMike Park,33,"}
                  className="font-mono text-xs"
                  data-testid="paste-csv-textarea" />
                <div className="flex justify-end">
                  <Button type="button" size="sm" onClick={handlePasteCSV} className="bg-[#00247D] hover:bg-[#001A5C] text-white" data-testid="paste-csv-import">
                    Import {pasteText.trim() ? `(${pasteText.trim().split(/\r?\n/).filter(Boolean).length} rows)` : ''}
                  </Button>
                </div>
              </div>
            )}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-[40px_1fr_80px_80px_80px_36px] gap-2 px-3 py-2 bg-slate-100 text-[10px] uppercase tracking-wide font-bold text-slate-500">
                <div></div>
                <div>Name</div>
                <div>Jersey</div>
                <div>Photo</div>
                <div className="text-center">Captain</div>
                <div></div>
              </div>
              <div className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <div key={i} className="grid grid-cols-[40px_1fr_80px_80px_80px_36px] gap-2 px-3 py-2 items-center">
                    <div className="text-xs text-slate-400 text-center">{i + 1}</div>
                    <Input value={r.name} onChange={e => update(i, 'name', e.target.value)}
                      placeholder="Player name" className="h-8 text-sm"
                      data-testid={`roster-row-name-${i}`} />
                    <Input value={r.jersey_number} onChange={e => update(i, 'jersey_number', e.target.value)}
                      placeholder="#" className="h-8 text-sm"
                      data-testid={`roster-row-jersey-${i}`} />
                    <div className="flex items-center gap-1">
                      {r.headshot_path ? (
                        <img src={`${API_URL}/api/files/${r.headshot_path}`} alt=""
                          className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200" />
                      )}
                      <Button type="button" variant="ghost" size="sm" className="h-7 px-1.5"
                        onClick={() => triggerUpload(i)}
                        data-testid={`roster-row-upload-${i}`}>
                        <Upload className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <button type="button" onClick={() => update(i, 'is_captain', !r.is_captain)}
                      className={`mx-auto p-1 rounded-md ${r.is_captain ? 'text-amber-500 bg-amber-50' : 'text-slate-300 hover:text-amber-500'}`}
                      title={r.is_captain ? 'Captain' : 'Mark captain'}
                      data-testid={`roster-row-captain-${i}`}>
                      <Star className={`w-4 h-4 ${r.is_captain ? 'fill-amber-500' : ''}`} />
                    </button>
                    <button type="button" onClick={() => removeRow(i)}
                      className="text-slate-400 hover:text-red-500 p-1"
                      data-testid={`roster-row-remove-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addRow} className="w-full"
              data-testid="roster-add-row-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Player
            </Button>
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200">
              <Button type="button" variant="outline" onClick={() => onOpenChange && onOpenChange(false)}>Cancel</Button>
              <Button type="button" onClick={save} disabled={saving}
                className="bg-[#00247D] hover:bg-[#001A5C] text-white" data-testid="roster-save-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Save Roster'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
      {team && (
        <MergePlayersDialog
          open={mergeOpen}
          onOpenChange={setMergeOpen}
          eventId={eventId}
          team={team}
          onMerged={() => {
            // After a merge, reload the roster so renames show immediately
            if (onSaved) onSaved();
          }}
        />
      )}
    </Dialog>
  );
}
